import { useEffect, useState, useRef } from "react";
import {
  collection, getDocs, doc, updateDoc, addDoc, deleteDoc,
  query, orderBy
} from "firebase/firestore";
import { db, storage } from "./firebaseConfig";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import "./index.css";

function Agenda() {
  const [items, setItems] = useState([]);
  const [error, setError] = useState("");
  const [selectedItem, setSelectedItem] = useState(null);
  const [editing, setEditing] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newText, setNewText] = useState("");
  const [newLink, setNewLink] = useState("");
  const [newBanner, setNewBanner] = useState(null);
  const [bannerPreview, setBannerPreview] = useState(null);
  const [newCategory, setNewCategory] = useState("");
  const [newCreatedDate, setNewCreatedDate] = useState("");
  const [newEndDate, setNewEndDate] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const textareaRef = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const q = query(collection(db, "agenda"), orderBy("created_time", "desc"));
        const snapshot = await getDocs(q);
        const list = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setItems(list);
      } catch {
        setError("Erro a carregar dados");
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (!selectedItem) {
      setNewCreatedDate("");
      setNewEndDate("");
      return;
    }

    let createdObj, endObj;
    if (selectedItem.created_time?.seconds) {
      createdObj = new Date(selectedItem.created_time.seconds * 1000);
    } else {
      createdObj = new Date(selectedItem.created_time);
    }
    setNewCreatedDate(!isNaN(createdObj.getTime()) ? createdObj.toISOString().slice(0, 10) : "");

    if (selectedItem.end_time?.seconds) {
      endObj = new Date(selectedItem.end_time.seconds * 1000);
    } else {
      endObj = new Date(selectedItem.end_time);
    }
    setNewEndDate(selectedItem.end_time ? (!isNaN(endObj.getTime()) ? endObj.toISOString().slice(0, 10) : "") : "");
  }, [selectedItem]);

  const saveField = async (id, field, value) => {
    const docRef = doc(db, "agenda", id);
    await updateDoc(docRef, { [field]: value });
  };

  const saveBanner = async (id) => {
    if (!newBanner) return;
    const bannerRef = ref(storage, `agenda/${newBanner.name}`);
    await uploadBytes(bannerRef, newBanner);
    const url = await getDownloadURL(bannerRef);
    await saveField(id, "banner", url);
    setNewBanner(null);
    setBannerPreview(null);
    return url;
  };

  const addAgendaItem = () => {
    const newItem = {
      id: "temp-" + Date.now(),
      title: "New Agenda",
      text: "",
      link: "",
      banner: "",
      category: "",
      created_time: new Date(),
      end_time: null
    };
    setItems(prev => [newItem, ...prev]);
    setSelectedItem(newItem);
    setEditing(true);
    setNewTitle("");
    setNewText("");
    setNewLink("");
    setNewCategory("");
    setNewBanner(null);
    setBannerPreview(null);
    setNewCreatedDate("");
    setNewEndDate("");
  };

  const deleteAgendaItem = async (id) => {
    if (!window.confirm("Tem certeza de que deseja excluir esta agenda da app?")) return;
    await deleteDoc(doc(db, "agenda", id));
    setItems(prev => prev.filter(d => d.id !== id));
    if (selectedItem?.id === id) setSelectedItem(null);
  };

  const saveAll = async () => {
    if (!selectedItem) return;
    let id = selectedItem.id;
    if (id.startsWith("temp-")) {
      const newDoc = {
        title: newTitle,
        text: newText,
        link: newLink,
        banner: "",
        category: newCategory,
        created_time: newCreatedDate ? new Date(newCreatedDate) : new Date(),
        end_time: newEndDate ? new Date(newEndDate) : null
      };
      const docRef = await addDoc(collection(db, "agenda"), newDoc);
      id = docRef.id;
      setItems(prev => prev.map(d => d.id === selectedItem.id ? { ...newDoc, id } : d));
    } else {
      await saveField(id, "title", newTitle);
      await saveField(id, "text", newText);
      await saveField(id, "link", newLink);
      await saveField(id, "category", newCategory);
      if (newCreatedDate) await saveField(id, "created_time", new Date(newCreatedDate));
      if (newEndDate) await saveField(id, "end_time", new Date(newEndDate));
    }

    let bannerURL = selectedItem.banner;
    if (newBanner) bannerURL = await saveBanner(id);

    setItems(prev => prev.map(d =>
      d.id === id ? {
        ...d,
        title: newTitle,
        text: newText,
        link: newLink,
        category: newCategory,
        created_time: newCreatedDate ? new Date(newCreatedDate) : selectedItem.created_time,
        end_time: newEndDate ? new Date(newEndDate) : selectedItem.end_time,
        banner: bannerURL
      } : d
    ));

    setSelectedItem(prev => ({
      ...prev,
      id,
      title: newTitle,
      text: newText,
      link: newLink,
      category: newCategory,
      created_time: newCreatedDate ? new Date(newCreatedDate) : prev.created_time,
      end_time: newEndDate ? new Date(newEndDate) : prev.end_time,
      banner: bannerURL
    }));

    setEditing(false);
    setNewBanner(null);
    setBannerPreview(null);
  };

  const filteredItems = items.filter(item =>
    item.title?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (error) return <p>{error}</p>;

  return (
    <div className="table-container">
      <div>
        <input
          type="text"
          placeholder="Procurar por título"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="input"
        />
        <button className="btn btn-add" onClick={addAgendaItem}>
          Nova Agenda
        </button>
      </div>

      {filteredItems.length === 0 ? (
        <p style={{ textAlign: "center", color: "#292f2c" }}>Sem dados</p>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th>Título</th>
              <th>Data de Início</th>
              <th>Data de Fim</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {filteredItems.map(item => (
              <tr key={item.id}>
                <td>{item.title}</td>
                <td>
                  {item.created_time
                    ? item.created_time.seconds
                      ? new Date(item.created_time.seconds * 1000).toLocaleDateString()
                      : new Date(item.created_time).toLocaleDateString()
                    : "—"}
                </td>
                <td>
                  {item.end_time
                    ? item.end_time.seconds
                      ? new Date(item.end_time.seconds * 1000).toLocaleDateString()
                      : new Date(item.end_time).toLocaleDateString()
                    : "—"}
                </td>
                <td>
                  <button
                    className="btn btn-add"
                    onClick={() => {
                      setSelectedItem(item);
                      setEditing(false);
                      setNewTitle(item.title);
                      setNewText(item.text);
                      setNewLink(item.link);
                      setNewCategory(item.category || "");
                      setNewBanner(null);
                      setBannerPreview(null);
                      setNewCreatedDate(item.created_time
                        ? item.created_time.seconds
                          ? new Date(item.created_time.seconds * 1000).toISOString().slice(0, 10)
                          : new Date(item.created_time).toISOString().slice(0, 10)
                        : ""
                      );
                      setNewEndDate(item.end_time
                        ? item.end_time.seconds
                          ? new Date(item.end_time.seconds * 1000).toISOString().slice(0, 10)
                          : new Date(item.end_time).toISOString().slice(0, 10)
                        : ""
                      );
                    }}
                  >
                    Abrir
                  </button>
                  <button className="btn btn-delete" onClick={() => deleteAgendaItem(item.id)}>
                    Apagar
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {selectedItem && (
        <div className="drawer">
          <div className="drawer-content">
            <label>Título:</label>
            {editing ? (
              <input type="text" value={newTitle} onChange={(e) => setNewTitle(e.target.value)} />
            ) : (
              <span>{selectedItem.title || "—"}</span>
            )}

            <br /><br /><label>Texto:</label>
            {editing ? (
              <textarea
                ref={textareaRef}
                value={newText}
                onChange={(e) => setNewText(e.target.value)}
                style={{ height: "150px", width: "100%" }}
              />
            ) : (
              <div>{selectedItem.text || "—"}</div>
            )}

            <br /><br /><label>Link:</label>
            {editing ? (
              <input type="text" value={newLink} onChange={(e) => setNewLink(e.target.value)} />
            ) : (
              selectedItem.link ? <a href={selectedItem.link} target="_blank" rel="noreferrer">{selectedItem.link}</a> : <span>—</span>
            )}

            <br /><br /><label>Categoria:</label>
            {editing ? (
              <input type="text" value={newCategory} onChange={(e) => setNewCategory(e.target.value)} />
            ) : (
              <span>{selectedItem.category || "Not defined"}</span>
            )}

            <br /><br /><label>Data de Início:</label>
            {editing ? (
              <input type="date" value={newCreatedDate} onChange={(e) => setNewCreatedDate(e.target.value)} />
            ) : (
              <span>
                {selectedItem.created_time
                  ? selectedItem.created_time.seconds
                    ? new Date(selectedItem.created_time.seconds * 1000).toLocaleDateString()
                    : new Date(selectedItem.created_time).toLocaleDateString()
                  : "—"}
              </span>
            )}

            <br /><br /><label>Data de Fim:</label>
            {editing ? (
              <input type="date" value={newEndDate} onChange={(e) => setNewEndDate(e.target.value)} />
            ) : (
              <span>
                {selectedItem.end_time
                  ? selectedItem.end_time.seconds
                    ? new Date(selectedItem.end_time.seconds * 1000).toLocaleDateString()
                    : new Date(selectedItem.end_time).toLocaleDateString()
                  : "—"}
              </span>
            )}

            <br /><br /><label>Banner:</label>
            <div className="banner-preview">
              {bannerPreview ? (
                <img src={bannerPreview} alt="Preview" className="banner-agenda" />
              ) : (
                selectedItem.banner && <img src={selectedItem.banner} alt="Banner" className="banner-agenda" />
              )}
            </div>
            {editing && (
              <input
                type="file"
                onChange={(e) => {
                  const file = e.target.files[0];
                  setNewBanner(file);
                  setBannerPreview(file ? URL.createObjectURL(file) : null);
                }}
              />
            )}
          </div>

          <div className="drawer-footer">
            {editing ? (
              <>
                <button className="btn btn-add" onClick={saveAll}>Guardar</button>
                <button
                  className="btn btn-cancel"
                  onClick={() => {
                    if (selectedItem.id.startsWith("temp-")) {
                      setItems(prev => prev.filter(d => d.id !== selectedItem.id));
                      setSelectedItem(null);
                    } else {
                      setEditing(false);
                    }
                  }}
                >
                  Cancelar
                </button>
                <button className="btn btn-cancel" onClick={() => setSelectedItem(null)}>Fechar</button>
              </>
            ) : (
              <>
                <button className="btn btn-add" onClick={() => setEditing(true)}>Editar</button>
                <button className="btn btn-cancel" onClick={() => setSelectedItem(null)}>Fechar</button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default Agenda;
